import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';
import { ApiHit } from '../../Api/ApiHit';
import { searchUser } from '../../Constant/Constant';
import { getSelectedAccount } from '../../Storage/Storage';

function UserMultiSelect({ selectedUsers, setSelectedUsers }) {
  const [users, setUsers] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch all users for dropdown
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    const account = getSelectedAccount();

    const json = {
      page: 1,
      limit: 100,
      search: {
        accountId: account?._id
      }
    };

    ApiHit(json, searchUser)
      .then((res) => {
        if (res?.statusCode === 200) {
          setUsers(res?.data?.docs || []);
        } else {
          console.error('Failed to fetch users:', res?.error);
        }
      })
      .catch((err) => {
        console.error('Error fetching users:', err);
      });
  };

  // MultiSelect handlers
  const toggleUserSelection = (user) => {
    const isSelected = selectedUsers.some(u => u._id === user._id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter(u => u._id !== user._id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const removeUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u._id !== userId));
  };

  const clearAllUsers = () => {
    setSelectedUsers([]);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.roleId?.roleName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="mt-5 max-w-md">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Select Users to View Cashbook
      </label>
      <div className="relative" ref={dropdownRef}>
        <div
          className="w-full min-h-[44px] py-2 px-3 bg-white border rounded-lg transition-colors cursor-pointer border-slate-300 hover:border-slate-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/20"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <div className="flex items-center justify-between min-h-[28px]">
            <div className="flex-1 flex flex-wrap gap-1 items-center">
              {selectedUsers.length === 0 ? (
                <span className="text-slate-400">Select users...</span>
              ) : (
                <>
                  {selectedUsers.slice(0, 3).map((user) => (
                    <span
                      key={user._id}
                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                    >
                      {user.name}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeUser(user._id);
                        }}
                        className="ml-1 hover:text-blue-600"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  {selectedUsers.length > 3 && (
                    <span className="text-sm text-gray-500">
                      +{selectedUsers.length - 3} more
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-1">
              {selectedUsers.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllUsers();
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X size={16} />
                </button>
              )}
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </div>

        {isDropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="max-h-60 overflow-auto">
              {filteredUsers.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  {searchTerm ? `No users found for "${searchTerm}"` : 'No users available'}
                </div>
              ) : (
                <div className="py-1">
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUsers.some(u => u._id === user._id);
                    return (
                      <div
                        key={user._id}
                        className={`px-3 py-2 cursor-pointer flex items-center justify-between transition-colors hover:bg-gray-50 ${
                          isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                        onClick={() => toggleUserSelection(user)}
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{user.name}</span>
                          <span className="text-xs text-gray-500">
                            {user.roleId?.roleName || 'No Role'} • {user.email}
                          </span>
                        </div>
                        {isSelected && (
                          <Check size={16} className="text-blue-600" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {selectedUsers.length > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          Viewing data for {selectedUsers.length} selected user{selectedUsers.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

export default UserMultiSelect;
